import { Injectable } from '@angular/core';
import { ColDef } from 'ag-grid-community';
import {
  AccessPermissionService,
  defaultColumn,
  DELETE_RECORD_LABEL,
  grid_widths_map,
} from '@piqet-angular/shared';
import { right } from '@popperjs/core';
import { ProjectsFacade, customEncode, encrypt } from '@piqet-angular/data';
import { Router } from '@angular/router';
import { NumericCellEditor } from 'libs/ui/src/lib/numeri-cell-editor/numeric-cell-editor.component';
import { cloneDeep } from 'lodash';
import { Subject } from 'rxjs';
import { SwalService } from 'libs/shared/src/lib/service/swal.service';
import { SharedService } from 'libs/data/src/lib/services/shared/shared.service';
@Injectable({
  providedIn: 'root',
})
export class ProjectScenarioDataGridService {
  projectScenarioDataPayload: any;
  event: any;

  copyScenario = new Subject<[any]>();
  copyScenarioData$ = this.copyScenario.asObservable();

  deleteScenario = new Subject<[any]>();
  deleteScenarioData$ = this.deleteScenario.asObservable();

  constructor(
    private projectsFacade: ProjectsFacade,
    private router: Router,
    private swalService: SwalService,
    private sharedService: SharedService,
    private accessService: AccessPermissionService
  ) {}

  projectScenarioDataColumn(headerName?: any, hasMultipleScenarios?: boolean): ColDef[] {
    const colDef: ColDef[] = [];
    colDef.push({
      ...defaultColumn,
      colId: 'ScenarioId',
      headerName: 'ScenarioId',
      field: 'ScenarioId',
      hide: true,
      suppressColumnsToolPanel: true,
    });
    colDef.push({
      ...defaultColumn,
      colId: '',
      headerName: '',
      field: '',
      rowDrag: hasMultipleScenarios,
      cellClass: 'scenarioDragIcon',
      minWidth: grid_widths_map.icon_md,
      maxWidth: grid_widths_map.icon_md,
      resizable: false,
    });
    colDef.push({
      ...defaultColumn,
      colId: 'scenarioPicture',
      field: 'scenarioPicture',
      headerName: 'Image',
      minWidth: grid_widths_map.sm_column_xxm,
      maxWidth: grid_widths_map.sm_column_xxm,
      cellClass: 'my-permission-cursor-pointer',
      cellRenderer: 'GridMyProjectImageComponent',
      wrapText: true,
      autoHeight: true,
      lockVisible: false,
      resizable: false,
      sortable: false,
    });

    colDef.push({
      ...defaultColumn,
      colId: 'Name',
      headerName: 'Scenario name',
      field: 'name',
      cellClass: 'my-permission-cursor-pointer',
      wrapText: true,
      autoHeight: true,
      lockVisible: true,
      resizable: false,
      sortable: true,
      cellRenderer: 'linkRenderer',
    });

    colDef.push({
      ...defaultColumn,
      colId: 'ScenarioCode',
      headerName: 'Scenario code',
      field: 'scenarioCode',
      cellClass: 'my-permission-cursor-pointer',
      wrapText: true,
      autoHeight: true,
      lockVisible: false,
      resizable: false,
      editable: false,
    });

    colDef.push({
      ...defaultColumn,
      colId: 'Status',
      headerName: 'Status',
      field: 'status',
      cellClass: 'my-permission-cursor-pointer',
      wrapText: true,
      autoHeight: true,
      lockVisible: false,
      resizable: false,
      editable: false,
    });

    colDef.push({
      ...defaultColumn,
      colId: 'Owner',
      headerName: 'Owner',
      field: 'owner',
      cellClass: 'my-permission-cursor-pointer',
      wrapText: true,
      autoHeight: true,
      lockVisible: false,
      resizable: false,
      editable: false,
    });

    colDef.push({
      ...defaultColumn,
      colId: 'Actions',
      headerName: 'Actions',
      field: 'actions',
      sortable: false,
      cellRendererParams: {
        showView: true,
        showCopy: this.accessService.hasPermission('edit', '', 'packaging'),
        showDelete: this.accessService.hasPermission('edit', '', 'packaging'),
        deleteData: (data: any) => {
          this.deleteConfirmation(data);
        },
        copyData: (data: any) => {
          this.copyScenario.next([data.scenarioId]);
        },
        copyDataEditRendarar: (data: any) => {
          return false;
        },
        viewData: (data: any) => {
          const valueToEncrypt = `{ "id": ${data.scenarioId}, "packageScenarioId": ${data.packageScenarioId}, "eId": ${data.scenarioId} }`;
          const encryptedValue = encrypt(valueToEncrypt);
          this.sharedService.scenarioNameFunc(data?.name);
          localStorage.setItem('scenarioName', data?.name);
          const encodedValue = encodeURIComponent(data.projectId);
          this.router.navigate(
            [
              `projects/projects-setting/${customEncode(
                btoa(encodedValue)
              )}/scenario-configuration`,
            ],
            {
              queryParams: {
                param: encryptedValue,
              },
            }
          );
        },
      },
      maxWidth: grid_widths_map.sm_column_xs,
      cellClass: 'my-permission-cursor-pointer',
      cellRenderer: 'actionRenderer',
      lockVisible: true,
      lockPosition: right,
      resizable: false,
    });
    return colDef;
  }

  getPayload(searchData: any, sortColumnState?: any, event?: any) {
    if (event) {
      this.event = event;
    }
    const currentPage = this.event?.goToPage
      ? this.event.goToPage
      : this.event?.next
      ? this.event.currentPage + 1
      : this.event?.prev
      ? this.event?.currentPage - 1
      : this.event?.gridApi?.paginationGetCurrentPage() + 1;
    const pageSize = this.event?.gridApi.paginationGetPageSize();
    const startCount = pageSize * (currentPage - 1);

    const columns = this.projectScenarioDataColumn()
      .map((item: any) => {
        return {
          searchable: true,
          name: item.colId,
          orderable: true,
          data: item.colId,
        };
      })
      .filter(value => value?.name != 'Actions');

    return (this.projectScenarioDataPayload = {
      search: {
        columnNameList: [],
        value: searchData || '',
      },
      columns: columns,
      order: [
        {
          columnName: sortColumnState?.columnName || 'ScenarioId',
          dir:
            sortColumnState?.dir[0].toUpperCase() + sortColumnState?.dir.substr(1).toLowerCase() ||
            'Asc',
        },
      ],
      start: startCount || 0,
      length: pageSize == Number(this.event?.totalRecord) ? Number('-1') : pageSize || 10,
      draw: currentPage || 1,
    });
  }

  deleteConfirmation(data: any) {
    this.swalService.confirm('Are you sure?', DELETE_RECORD_LABEL).then(confirmed => {
      if (confirmed) {
        this.deleteScenario.next([data]);
      } else {
      }
    });
  }
}
